// Globals
import type { MaybeRef } from "vue";
import type { UseSeoMetaInput } from "@unhead/schema";
import { version } from "~/package.json";

// Types
import type { ContentfulPageComposableOptions } from "@citizenm/lobby/types/contentful";
import type {
  ContentfulPageResponse,
  ContentfulPageResponseWithoutHero,
} from "@citizenm/foundation/shared/types/contentful";

export default function useContentfulPage(
  contentfulPage: MaybeRef<
    ContentfulPageResponseWithoutHero<any> | ContentfulPageResponse | null
  >,
  options: Partial<ContentfulPageComposableOptions> = {},
) {
  const tracking = useTracking();
  const { setSeoMeta, pageTypeToTrack, ecommerceStage = "none" } = options;
  const { trackPageView } = usePageViewTracking(pageTypeToTrack);

  const _page = unref(contentfulPage);

  const heroContent = computed(() => {
    if (!(_page && "hero" in _page)) {
      return null;
    }

    return {
      fields: {
        content: [
          {
            ..._page.hero,
            fields: {
              ..._page.hero.fields,
              titleHeadlineLevel: 1,
            },
          },
        ],
      },
    };
  });

  const meta = computed(() => _page?.meta?.fields || null);

  if (setSeoMeta) {
    const seoBaseMeta = meta.value
      ? ({
          ogDescription:
            meta.value.socialDescription || meta.value.seoDescription,
          description: meta.value.seoDescription,
          ogTitle: meta.value.socialTitle || meta.value.seoTitle,
          title: meta.value.seoTitle,
          twitterCard: "summary_large_image",
        } satisfies UseSeoMetaInput)
      : {};

    useSeoMeta({
      ...seoBaseMeta,
      robots: {
        noindex: _page?.indexAndFollow === "no",
        nofollow: _page?.indexAndFollow === "no",
      },
    });
  }

  if (import.meta.client && pageTypeToTrack) {
    trackPageView({
      title: meta.value?.seoTitle,
      ecommerceStage,
    });

    tracking.trackPageInfo({
      pageType: pageTypeToTrack,
      pageTitle: meta.value?.seoTitle,
      version,
    });
  }

  return {
    heroContent,
    meta,
  };
}
